import { Link } from "@tanstack/react-router"
import { useState, useEffect } from "react"
import { Menu, X, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ThemeToggle } from "@/components/theme-toggle"

const menuSections = [
  {
    title: "Product",
    links: [
      { label: "All Features", href: "/features" },
      { label: "Integrations", href: "/features/integrations" },
      { label: "AI Gateway", href: "/features/ai-gateway" },
      { label: "Agents Platform", href: "/features/agents-platform" },
      { label: "Security", href: "/features/security" },
      { label: "Analytics", href: "/features/analytics" },
      { label: "Workflow Engine", href: "/features/workflow-engine" },
      { label: "SSO & SAML", href: "/features/sso-saml" },
    ]
  },
  {
    title: "Solutions",
    links: [
      { label: "All Solutions", href: "/use-cases" },
      { label: "API Management", href: "/use-cases/api-management" },
      { label: "AI Automation", href: "/use-cases/ai-automation" },
      { label: "Data Synchronization", href: "/use-cases/data-synchronization" },
      { label: "FinOps", href: "/use-cases/finops" },
      { label: "Enterprise Security", href: "/use-cases/enterprise-security" },
    ]
  },
  {
    title: "Learn",
    links: [
      { label: "All Concepts", href: "/concepts" },
      { label: "Comparisons", href: "/comparisons" },
    ]
  },
  {
    title: "Company",
    links: [
      { label: "Pricing", href: "/info/pricing" },
      { label: "About", href: "/info/about" },
      { label: "Contact", href: "/info/contact" },
    ]
  }
]

export function HeaderMinimal() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    const hasAccessToken = document.cookie.includes('matricks_access_token')
    const hasRefreshToken = document.cookie.includes('matricks_refresh_token')
    setIsAuthenticated(hasAccessToken || hasRefreshToken)
  }, [])

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMenuOpen])

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between px-6">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
              <span className="text-sm font-bold text-primary-foreground">M</span>
            </div>
            <span className="text-xl font-bold">Matricks.ai</span>
          </Link>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            <ThemeToggle />
            <div className="hidden sm:flex items-center space-x-2">
              {isAuthenticated ? (
                <Button asChild>
                  <Link to="/overview">Dashboard</Link>
                </Button>
              ) : (
                <>
                  <Button variant="ghost" asChild>
                    <Link to="/login">Sign In</Link>
                  </Button>
                  <Button asChild>
                    <Link to="/login">Get Started</Link>
                  </Button>
                </>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Full-Page Mobile Menu Overlay */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-40 bg-background overflow-y-auto">
          {/* Menu Header */}
          <div className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur">
            <div className="container flex h-16 items-center justify-between px-6">
              <Link to="/" className="flex items-center space-x-2" onClick={() => setIsMenuOpen(false)}>
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                  <span className="text-sm font-bold text-primary-foreground">M</span>
                </div>
                <span className="text-xl font-bold">Matricks.ai</span>
              </Link>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMenuOpen(false)}
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Menu Content */}
          <div className="container px-6 py-8">
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {menuSections.map((section) => (
                <div key={section.title}>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                    {section.title}
                  </h3>
                  <ul className="space-y-3">
                    {section.links.map((link) => (
                      <li key={link.href}>
                        <Link
                          to={link.href}
                          className="text-base hover:text-primary transition-colors flex items-center group"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {link.label}
                          <ArrowRight className="ml-2 h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Auth Buttons in Menu */}
            <div className="mt-12 pt-8 border-t space-y-4">
              {isAuthenticated ? (
                <Button asChild className="w-full sm:w-auto">
                  <Link to="/overview" onClick={() => setIsMenuOpen(false)}>
                    Go to Dashboard
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              ) : (
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button variant="outline" asChild className="w-full sm:w-auto">
                    <Link to="/login" onClick={() => setIsMenuOpen(false)}>Sign In</Link>
                  </Button>
                  <Button asChild className="w-full sm:w-auto">
                    <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                      Get Started
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </>
  )
}
